import { utc as momentUTC } from 'moment-timezone';
import { getToday } from 'sonata-react-components';
import { worldTimezones } from 'sonata-static-storage/lib/samples/worldTimezones.samples';

import {
  getFlightRanges,
  getLineItemMarkers
} from '@/components/ioSettings/InsertionOrderForm/FlightSettings/flightSettings.helpers';
import { withFlightColors } from '@/helpers/ioSettings/flightColors.helper';

import type { Moment } from 'moment-timezone';
import type {
  DatePickerMarker,
  DatePickerOccupiedRange,
  DatePickerUIRangeProps,
  DateRangeValue
} from 'sonata-react-components';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { LineItemSchedule } from '@/components/ioSettings/InsertionOrderForm/hooks/useIoLineItemSchedules';

export const LINE_ITEM_TIMEZONE = 'UTC';
const ENDED_EDITABLE_DAYS = 30;

/** IO flights painted in their colour (as in Flight Settings) but not blocking: a line item may cross them. */
export const getIoFlightRanges = (
  flights: InsertionOrderFlight[],
  getFallbackLabel: (index: number) => string
): DatePickerOccupiedRange[] =>
  getFlightRanges(
    withFlightColors(flights).map(flight => ({ ...flight, _id: String(flight._id) })),
    getFallbackLabel
  ).map(range => ({ ...range, blocking: false }));

/**
 * The IO's line items as markers, coloured as in the IO picker (index over all the IO's line items). The line item
 * being edited follows its live dates; a new one (not saved yet) is added last.
 */
export const getIoLineItemMarkers = (
  lineItems: LineItemSchedule[],
  current: Pick<LineItemSchedule, '_id' | 'beginDate' | 'endDate' | 'name'>
): DatePickerMarker[] => {
  const isSaved = lineItems.some(lineItem => String(lineItem._id) === current._id);
  const withCurrent = isSaved
    ? lineItems.map(lineItem => (String(lineItem._id) === current._id ? { ...lineItem, ...current } : lineItem))
    : [...lineItems, current];
  return getLineItemMarkers(withCurrent);
};

type PastRules = Pick<DatePickerUIRangeProps, 'disabled' | 'disablePast' | 'lockedStart'>;

/**
 * Same rules as the old react-dates picker, which blocked every day before today: draft → no past days; active →
 * no past days and START locked once started (a future START can still move earlier, down to today); active and
 * ended more than 30 days ago → read-only (until then END stays extendable, from today on).
 */
export const getLineItemPastRules = (completed: boolean, beginDate: Date | null, endDate: Date | null): PastRules => {
  if (!completed) {
    return { disablePast: true };
  }
  const today = getToday(LINE_ITEM_TIMEZONE);
  if (endDate && momentUTC(endDate).startOf('day').isBefore(today.clone().subtract(ENDED_EDITABLE_DAYS, 'day'))) {
    return { disabled: true };
  }
  const lockedStart = Boolean(beginDate && momentUTC(beginDate).startOf('day').isSameOrBefore(today, 'day'));
  return { disablePast: true, lockedStart };
};

/** Picked day → UTC-midnight Date, exactly as the old react-dates handler stored it. */
export const toLineItemDate = (day: Moment | null): Date | null =>
  day ? momentUTC(day.startOf('day').format('YYYY-MM-DD')).toDate() : null;

export const toPickerValue = (beginDate: Date | null, endDate: Date | null): DateRangeValue => ({
  endDate: endDate ? momentUTC(endDate).startOf('day') : null,
  startDate: beginDate ? momentUTC(beginDate).startOf('day') : null
});

export function getAllTimeZones() {
  const allTimeZones = worldTimezones
    .filter(timezoneGroup => timezoneGroup.group !== 'UTC' && timezoneGroup.group !== 'Manual Offsets')
    .map(timezoneGroup => timezoneGroup.zones)
    .flat();
  return allTimeZones.sort().map(option => {
    return {
      id: option.value,
      label: option.value
    };
  });
}
